import Toast from "react-native-toast-message";
import { queryClient } from "../data/client";
import ErrorHandler from "./ErrorHandler";
import { QueryKeys } from "./react-query/QueryKeys";
import { getMessageById, getCommentById } from "../data/connect";



async function mobileNotificationHandler(newNotification, path, router){
    const notificationData = newNotification.request.content.data

    if(!notificationData){
        return;
    }

    try{
        queryClient.setQueryData(QueryKeys.people.loggedInUser(), old => ({
            ...(old || {}),
            Notifications: [
                {...notificationData},
                ...(old?.Notifications || [])
            ]
        }))

        // Always sync the relevant query cache, whether or not we suppress the alert —
        // this is what replaces the native realtime broadcast channels.
        await overwriteData(notificationData);

        if(path == notificationData?.path){
            //Don't show notification popup if the user is on that exact screen
            return {
                shouldShowAlert: false,
                shouldPlaySound: false,
                shouldSetBadge: false,
            }
        }
        else{
            showInAppNotification(notificationData, router);
        }
    }
    catch(e){
        ErrorHandler(e);
    }
}


const desktopNotificationHandler = (notificationData, path, router, messageChanged, pathChanged) => {
    if(!notificationData){
        return;
    }

    queryClient.setQueryData(QueryKeys.people.loggedInUser(), old => {
        return {...(old || {}),
            Notifications: [
                notificationData,
                ...(old?.Notifications || []).filter(n => n.id != notificationData.id)
            ]
        }
    })

    if(path == notificationData?.path){
        //Dont show notification popup if the user is on that screen
        return {
            shouldShowAlert: false,
            shouldPlaySound: false,
            shouldSetBadge: false,
        }
    }
    else{
        if(messageChanged || pathChanged){
            showInAppNotification(notificationData, router);
            overwriteData(notificationData).catch(ErrorHandler);
        }
    }
}



const showInAppNotification = (notificationData, router) => {
    Toast.show({
        type: "notification",
        text1: notificationData?.title,
        text2: notificationData?.message,
        onPress: () => {
            router.push(notificationData?.path);
            Toast.hide()
        },
    })
}

const overwriteData = async (notificationData) => {
    // Push payloads use notificationTypeID; web broadcast raw DB rows use notificationType (column name).
    const type = notificationData?.notificationTypeID || notificationData?.notificationType;

    switch(type){
        case "FORM_ENTRY_SUBMITTED": {
            queryClient.invalidateQueries({queryKey: QueryKeys.forms.all()});
            if(notificationData?.parameters?.formID) queryClient.invalidateQueries({queryKey: QueryKeys.formEntries.byFormId(notificationData.parameters.formID)});
            if(notificationData?.parameters?.entryID) queryClient.invalidateQueries({queryKey: QueryKeys.formEntries.byId(notificationData.parameters.entryID)});
            break;
        }
        case "MESSAGE_RECEIVED": {
            const conversationID = notificationData?.parameters?.conversationID;
            const messageID = notificationData?.parameters?.messageID;
            if(!conversationID || !messageID) break;

            //Only bother fetching if this conversation's messages are already cached (i.e. user has opened it this session)
            const cached = queryClient.getQueryData(QueryKeys.messages.byConversationId(conversationID));
            if(!cached) break;

            const message = await getMessageById(messageID);
            if(!message) break;

            queryClient.setQueryData(QueryKeys.messages.byConversationId(conversationID), old => {
                if(!old) return old;
                if(old.pages?.some(page => page?.data?.some(m => m.id == message.id))) return old; //dedupe
                const pages = [...old.pages];
                pages[0] = {...pages[0], data: [message, ...(pages[0]?.data || [])]};
                return {...old, pages};
            });

            const loggedInUserID = queryClient.getQueryData(QueryKeys.people.loggedInUser())?.id;
            queryClient.setQueryData(QueryKeys.conversations.byUserId(loggedInUserID), old => {
                if(!old) return old;
                const idx = old.findIndex(c => c.id == conversationID);
                if(idx == -1) return old;
                const newData = [...old];
                newData[idx] = {...newData[idx], lastMessage: message};
                newData.unshift(newData.splice(idx, 1)[0]);
                return newData;
            });
            break;
        }
        case "NEW_COMMENT": {
            const postID = notificationData?.parameters?.postID;
            const commentID = notificationData?.parameters?.commentID;
            if(!postID || !commentID) break;

            const cached = queryClient.getQueryData(QueryKeys.comments.byPostId(postID));
            if(!cached) break;

            const comment = await getCommentById(commentID);
            if(!comment) break;

            queryClient.setQueryData(QueryKeys.comments.byPostId(postID), old => {
                if(!old) return old;
                if(old.pages?.some(page => page?.data?.some(c => c.id == comment.id))) return old; //dedupe
                const pages = [...old.pages];
                pages[0] = {...pages[0], data: [comment, ...pages[0].data]};
                return {...old, pages};
            });
            break;
        }
        case "ORGANIZATION_ANNOUNCEMENT": {
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
            queryClient.invalidateQueries({queryKey: QueryKeys.boards.all()});
            if(notificationData?.parameters?.boardID) queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(notificationData.parameters.boardID)});
            break;
        }
        case "NEW_POST": {
            // parameters doesn't carry boardID today; path is `connect/boards/${boardID}/feed`.
            // Low-frequency/list-preview case — invalidateQueries here is fine (not a hot path like messages/comments).
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
            break;
        }
        case "TASK_ASSIGNED":
        case "TASK_RESPONSE": {
            // taskInstanceID isn't in parameters; parse from path (`tasks/instances/${id}`)
            const match = notificationData?.path?.match(/tasks\/instances\/(.+)$/);
            if(match) queryClient.invalidateQueries({queryKey: QueryKeys.taskInstances.byId(match[1])});
            break;
        }
        case "NEW_USER_REQUEST": {
            queryClient.invalidateQueries({queryKey: QueryKeys.registrations.all()});
            break;
        }
    }
}

export { mobileNotificationHandler, desktopNotificationHandler }